import { AfterViewInit, Component, OnInit } from '@angular/core';
import { rosterProfileIconUrl } from '../../utils/roster-image';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RosterService } from '../../services/roster.service';
import { Roster } from '../../models/roster';
import { Exclusivity } from '../../models/exclusivity';
import { GanttTask } from '../../models/ganttTask';
import { LoginService } from '../../services/login.service';
import { UserRole, User } from '../../models/user';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-roster',
  imports: [CommonModule, FormsModule],
  templateUrl: './roster.component.html',
  styleUrl: './roster.component.css'
})
export class RosterComponent implements OnInit {
  roster: Roster | null = null;
  profileIconUrl = rosterProfileIconUrl;
  agentUsers: User[] = [];
  sourceUsers: User[] = [];

  get isAdmin(): boolean { return this.loginService.user.role === UserRole.Admin; }
  // Admin, Agent, Scouting, Casting: the same roles the API allows by default.
  get isStaff(): boolean {
    const role = this.loginService.user.role;
    return role === UserRole.Admin || role === UserRole.Agent || role === UserRole.Scouting || role === UserRole.Casting;
  }

  resolveUserId(id: string): string { return this.userService.resolveUserId(id); }

  /** Sign date for the date input: trims any time portion, e.g. "2026-05-24". */
  formatSignDate(dateStr: string): string { return dateStr ? dateStr.substring(0, 10) : ''; }

  onSignDateChange(value: string): void {
    if (!this.roster) return;
    this.roster.signDate = value;
    this.onRosterChangeField('signDate', value || null);
  }

  exclusivities: Exclusivity[] = [];
  // Exclusivities that belong to this creator's campaigns (each carries campaignName). The roster's
  // own, pre-existing ones are on this.roster.exclusivities. Loaded when the Exclusivity tab opens.
  campaignExclusivities: Exclusivity[] = [];

  showTikTokData: boolean = false;
  showInstagramData: boolean = false;
  showYoutubeData: boolean = false;
  activeTab: string = 'properties';

  gantt: any = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private rosterService: RosterService,
    private loginService: LoginService,
    public userService: UserService
  ) {}

  ngOnInit() {
    const creatorId = this.route.snapshot.paramMap.get('id');


      if (creatorId) {
        this.rosterService.getRoster(creatorId).subscribe((result) => {
          this.roster = result;
          console.log(this.roster);

          // Build the derived exclusivities list from the authoritative roster
          this.rebuildExclusivitiesFromRoster();
        });
      }

    this.userService.getUsers().subscribe(users => {
      this.agentUsers = users.filter(u => u.role === UserRole.Agent || u.role === UserRole.Scouting || u.role === UserRole.Casting);
      this.sourceUsers = users.filter(u => u.role === UserRole.Agent || u.role === UserRole.Scouting || u.role === UserRole.Casting || u.role === UserRole.Admin);
    });
  }

  onRosterChangeField(field: string, value: any): void {
    if (!this.roster) return;
    const id = this.roster.id;

    this.rosterService.patchRosterField(id, field, value).subscribe({
      next: () => {
        console.log('Roster field updated');
      },
      error: (err) => {
        if (err.status === 412) {
          console.error('Roster update failed due to conflict. Refresh to get the latest version.', err);
        } else {
          console.error('Failed to patch roster field', err);
        }
      }
    });
  }

  calendarSyncing = false;
  calendarSyncMessage = '';

  // Checking the box saves the flag and then runs the initial "add all tasks" sync; unchecking
  // saves it and the same sync removes the creator's events.
  onCalendarEventsChange(enabled: boolean): void {
    if (!this.roster) return;
    const roster = this.roster;

    this.calendarSyncing = true;
    this.calendarSyncMessage = '';

    this.rosterService.patchRosterField(roster.id, 'enableCalendarEvents', enabled).subscribe({
      next: () => this.syncCalendar(),
      error: (err) => {
        console.error('Failed to patch enableCalendarEvents', err);
        roster.enableCalendarEvents = !enabled;
        this.calendarSyncing = false;
        this.calendarSyncMessage = 'Could not save the calendar setting.';
      }
    });
  }

  syncCalendar(): void {
    if (!this.roster) return;

    this.calendarSyncing = true;
    this.calendarSyncMessage = '';

    this.rosterService.reconcileCalendar(this.roster.id).subscribe({
      next: (result) => {
        this.calendarSyncing = false;
        const count = (action: string) => result.actions.filter(a => a.action === action && !a.error).length;
        this.calendarSyncMessage =
          `Calendar synced: ${count('insert')} added, ${count('update')} updated, ${count('delete')} removed`
          + (result.failed ? `, ${result.failed} failed (use Sync now to retry).` : '.');
      },
      error: (err) => {
        console.error('Calendar sync failed', err);
        this.calendarSyncing = false;
        // A 400 carries a reason that retrying cannot fix (e.g. the creator's email no longer exists).
        this.calendarSyncMessage = err?.status === 400 && typeof err.error === 'string' && err.error
          ? `Calendar not synced: ${err.error}`
          : 'Calendar sync failed. Use Sync now to retry.';
      }
    });
  }

  patchExclusivity(exclusivityId: string, field: string, value: any): void {
    if (!this.roster) return;
    const rosterId = this.roster.id;
    this.rosterService.patchExclusivityField(rosterId, exclusivityId, field, value).subscribe({
      next: (resp) => {
        // Server returns the updated Exclusivity; the service merges it into the roster cache.
        // Re-read the authoritative roster from the service and rebuild the derived list.
        this.rosterService.getRoster(rosterId).subscribe((r) => {
          if (r) {
            this.roster = r;
            this.rebuildExclusivitiesFromRoster();
          }
        });
      },
      error: (err) => {
        console.error('Failed to patch exclusivity field', err);
      }
    });
  }

  // Rebuild the component's derived exclusivities[] from the authoritative this.roster
  // and from this.campaignExclusivities (loaded separately when the Exclusivity tab is activated).
  rebuildExclusivitiesFromRoster(): void {
    this.exclusivities = [];
    if (!this.roster) return;

    if (!this.roster.exclusivities) this.roster.exclusivities = [];
    this.roster.exclusivities.forEach((e) => {
      (e as any).campaignName = 'Pre-Existing Exclusivity';
      this.exclusivities.push(e);
    });

    this.campaignExclusivities.forEach(e => this.exclusivities.push(e));

    // Sort by start date ascending
    this.exclusivities.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));

    // Ensure Gantt is initialized / refreshed after we rebuild exclusivities
    // (delay slightly to allow view updates if needed)
    setTimeout(() => this.tryInitGantt(), 0);
  }

  addExclusivity(event: Event): void {
    event.preventDefault();
    if (!this.roster) return;
    // Create a lightweight blank exclusivity and push into local list — server should assign id on save
    const newEx: Exclusivity = {
      id: crypto.randomUUID(),
      startDate: new Date().toISOString().slice(0,10),
      endDate: new Date().toISOString().slice(0,10),
      scope: 'New Exclusivity'
    } as any;
    this.exclusivities.push(newEx);

    // Also persist to server and update local roster/exclusivities when server returns updated roster
    this.rosterService.addExclusivity(this.roster.id, newEx).subscribe({
      next: (updated) => {
        if (updated) {
          this.roster = updated;
          this.exclusivities = (updated.exclusivities || []).slice();
        }
      },
      error: (err) => {
        console.error('Failed to add exclusivity', err);
      }
    });


  }

  deleteExclusivity(exclusivityId: string): void {
    if (!this.roster) return;
    const rosterId = this.roster.id;

    // Call the service to delete on the server, then refresh the authoritative roster from cache
    this.rosterService.deleteExclusivity(rosterId, exclusivityId).subscribe({
      next: () => {
        // Re-read authoritative roster and rebuild the derived exclusivities list
        this.rosterService.getRoster(rosterId).subscribe((r) => {
          if (r) {
            this.roster = r;
            this.rebuildExclusivitiesFromRoster();
          }
        });
      },
      error: (err) => {
        console.error('Failed to delete exclusivity', err);
      }
    });
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'exclusivity' && this.roster) {
      this.loadExclusivityCampaigns();
    }
  }

  private loadExclusivityCampaigns(): void {
    if (!this.roster) return;
    // rosters/{id}/exclusivities merges the roster's own exclusivities with those of the
    // roster's campaigns and names the campaign on each campaign-level one; keep only those.
    this.rosterService.getRosterExclusivities(this.roster.id).subscribe({
      next: (all) => {
        this.campaignExclusivities = all.filter(e => !!(e as any).campaignName);
        this.rebuildExclusivitiesFromRoster();
      },
      error: (err) => console.error('Failed to load campaign exclusivities', err)
    });
  }

  tryInitGantt(): void {
    // Don't render an empty chart — the template hides #gantt when exclusivities is empty.
    if (!this.exclusivities || this.exclusivities.length === 0) {
      this.gantt = null;
      return;
    }

    // If no #gantt element in DOM yet, only retry when on the exclusivity tab.
    const el = document.querySelector('#gantt');
    if (!el) {
      if (this.activeTab === 'exclusivity') {
        setTimeout(() => this.tryInitGantt(), 100);
      }
      return;
    }

    // Build tasks expected by frappe-gantt from Exclusivity[]
    const tasks = (this.exclusivities || []).map((e: Exclusivity) => {
      // ensure valid end date (frappe-gantt expects end strictly after start)
      let start = e.startDate || '';
      let end = e.endDate || start;
      // If end equals start (or is before), bump end by one day so the bar is visible
      try {
        const s = new Date(start);
        let en = new Date(end);
        if (isNaN(s.getTime())) {
          // invalid start: fallback to today
          s.setTime(Date.now());
          start = s.toISOString().slice(0,10);
        }
        if (isNaN(en.getTime()) || en.getTime() <= s.getTime()) {
          // set end to start + 1 day
          en = new Date(s.getTime() + 24 * 60 * 60 * 1000);
          end = en.toISOString().slice(0,10);
        }
      } catch (err) {
        // ignore and use raw values
      }

      return {
        id: e.id,
        // use the parent campaign name as the visible bar label
        name: ((e as any).campaignName || e.scope || ''),
        start: start,
        end: end,
        // keep the original scope on the task for popup usage
        scope: e.scope,
        dependencies: '',
        color: '#007bff'
      } as any;
    });

    // Sort tasks by start date so bars are ordered predictably in the chart.
    tasks.sort((a: any, b: any) => {
      const da = new Date(a.start || '');
      const db = new Date(b.start || '');
      return (isNaN(da.getTime()) ? 0 : da.getTime()) - (isNaN(db.getTime()) ? 0 : db.getTime());
    });

    // Compute view bounds: min task start − 6 months to max task end + 6 months
    const taskDates = tasks.flatMap((t: any) => [new Date(t.start), new Date(t.end)])
      .filter((d: Date) => !isNaN(d.getTime()));
    const minMs = Math.min(...taskDates.map((d: Date) => d.getTime()));
    const maxMs = Math.max(...taskDates.map((d: Date) => d.getTime()));
    const viewStart = new Date(minMs);
    viewStart.setMonth(viewStart.getMonth() - 6);
    const viewEnd = new Date(maxMs);
    viewEnd.setMonth(viewEnd.getMonth() + 6);
    const viewStartStr = viewStart.toISOString().slice(0, 10);
    const viewEndStr = viewEnd.toISOString().slice(0, 10);

    // Always recreate the chart so start_date/end_date bounds are applied on refresh too
    if (this.gantt) {
      el.innerHTML = '';
      this.gantt = null;
    }
    this.gantt = new (window as any).Gantt('#gantt', tasks, {
          view_mode: 'Month',
          popup: this.rosterPopupHtml.bind(this),
          view_mode_select: true,
          readonly: true,
          start_date: viewStartStr,
          end_date: viewEndStr
        });

    // Block wheel events so Frappe Gantt can't zoom/scroll on mouse wheel.
    // Use capture:true so this fires before Frappe Gantt's own inner listeners.
    // Re-register each time since the element may have been recreated.
    const wrapper = document.querySelector('.gantt-scroll-wrapper');
    if (wrapper) {
      wrapper.addEventListener('wheel', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, { passive: false, capture: true });
    }



  }

  //Custom popup handler - only show title and subtitle; avoid showing 'Progress'
  rosterPopupHtml(task: any) {
    try {
      // Title should be the campaign name (we stored it on the task.name)
      const title = task.task.name || '';
      const scope = task.task.scope || '';
      task.set_title(title);

      // Subtitle contains the full scope text and the date range
      let subtitle = '';
      if (scope) subtitle += `${scope}<br><br>`;
      subtitle += `Start: ${task.task.start}<br>End: ${task.task.end}`;
      task.set_subtitle(subtitle);

      // Optionally add actions (none by default for roster view)
      // task.add_action('View', () => { console.log('View clicked', task); });
    } catch (err) {
      console.warn('Error building roster popup:', err);
    }
  }

}
